import {
  CurrencyAdornment,
  CurrencyAutocomplete,
  CurrencyCode,
  DD,
  DT,
  FormField,
  getCurrencySymbolOrUndefined,
  GridCol,
  GridRow,
  Input,
  isDefinedNumber,
  isDefinedString,
  Select,
  toCurrencyStringOrUndefined,
  Typography,
} from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { Control, useWatch } from 'react-hook-form';
import { FormValues, FormPath } from '../agreement-conditions-card.component';
import { SecurityType } from '@client/api';
import { useTenantSettingsDefaults } from '../../../../../../hooks';

const SECURITY_TYPE_OPTIONS: SecurityType[] = ['BankDeposit', 'BankGuarantee', 'Cash', 'Other'];

interface SecurityDepositProps {
  isFetching: boolean;
  editing: boolean;
  control: Control<FormValues>;
  securityType?: SecurityType | null;
  securityAmount?: number | null;
  securityCurrencyCode?: CurrencyCode | null;
}

export const SecurityDeposit = ({
  isFetching,
  editing,
  control,
  securityType,
  securityAmount,
  securityCurrencyCode,
}: SecurityDepositProps) => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { defaultCurrencyCode } = useTenantSettingsDefaults();

  const unitCurrencyCode = useWatch({ control, name: 'security.amount.currency.code', defaultValue: '' });
  const unitCurrencySymbol = getCurrencySymbolOrUndefined(unitCurrencyCode);

  return (
    <div className="py-2" data-testid="security-deposit">
      <Typography variant="none" className="mb-4 text-brand-primary font-medium uppercase text-xs">
        {tGlobal('agreement.security')}
      </Typography>

      <GridRow cols={3} spacing={2} className="items-center">
        <GridCol span={1}>
          <DT>{tGlobal('agreement.securityType')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isDefinedString(securityType)}>{tType(`SecurityType.${securityType}`)}</DD>
          ) : (
            <FormField<FormValues, FormPath>
              name="security.type"
              render={({ field }) => (
                <Select size="medium" {...field}>
                  <option value="" />
                  {SECURITY_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {tType(`SecurityType.${type}`)}
                    </option>
                  ))}
                </Select>
              )}
            />
          )}
        </GridCol>

        <GridCol span={1}>
          <DT>{tGlobal('agreement.securityAmount')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isDefinedNumber(securityAmount)}>
              {toCurrencyStringOrUndefined(securityAmount ?? undefined, undefined, {
                currency: securityCurrencyCode ?? undefined,
              })}
            </DD>
          ) : (
            <GridRow cols={3} spacingX={1}>
              <GridCol span={2}>
                <FormField<FormValues, FormPath>
                  name="security.amount.amount"
                  render={({ field }) => (
                    <CurrencyAdornment position="start" size="small" symbol={unitCurrencySymbol}>
                      <Input
                        type="number"
                        size="medium"
                        className={
                          unitCurrencySymbol &&
                          (typeof unitCurrencySymbol === 'string' && unitCurrencySymbol.length >= 3 ? 'pl-8' : 'pl-4')
                        }
                        {...field}
                      />
                    </CurrencyAdornment>
                  )}
                />
              </GridCol>
              <GridCol span={1}>
                <FormField<FormValues, FormPath>
                  name="security.amount.currency.code"
                  render={({ field: { value, ref, ...field } }) => (
                    <CurrencyAutocomplete
                      size="medium"
                      selected={value}
                      defaultCurrencyCode={defaultCurrencyCode}
                      {...field}
                    />
                  )}
                />
              </GridCol>
            </GridRow>
          )}
        </GridCol>
      </GridRow>
    </div>
  );
};
